import { z } from 'zod';

import {
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  score10Schema,
  sortOrderSchema,
  ulidSchema,
} from '../common';
import type { UnsupportedClaim } from '../comment-validation';
import {
  agentCommandStatusSchema,
  commentCancelRecallOutcomeSchema,
  commentJobStatusSchema,
  commentModeSchema,
  commentRejectionReasonSchema,
  commentReviewFlagSchema,
  commentSourceSchema,
  platformSchema,
  postIntentSchema,
  unsupportedClaimKindSchema,
} from '../enums';
import { queryBooleanSchema } from './posts';

/**
 * `/comments` DTOs — settings, templates, generation, jobs and results.
 *
 * Three rules shape every schema in this file:
 *   - No AI output reaches an agent unvalidated. Generation returns a
 *     *candidate* plus its validation verdict; only an approved, validated
 *     candidate becomes a job, and only a job becomes a `comment.execute`
 *     command with a `commandId` and an `expiresAt`.
 *   - Both rate limiters apply, server-side and agent-side. When the agent's
 *     local guard refuses, the refusal wins — the server records the rejection
 *     rather than retrying around it.
 *   - Automatic modes are opt-in. `MANUAL` and `AI_DRAFT` are the defaults;
 *     `TEMPLATE_AUTO` and `AI_AUTO` only ever come from a deliberate change.
 */

// ---------------------------------------------------------------------------
// GET /projects/:id/comment-sources
// ---------------------------------------------------------------------------

/**
 * Where a comment's text may come from, and whether that source works *right
 * now* — as data.
 *
 * The dashboard must never derive this. "AI_AUTO is unusable because the
 * workspace has no API key" depends on `ai_provider_settings`, on whether the
 * deployment carries a platform key (which the API cannot see — `apiEnvSchema`
 * has no `OPENAI_*` by design), and on whether anything is consuming the
 * `ai-comment` queue. A client that guessed would be wrong the moment any of
 * those changed, and the failure mode is the silent one: the user picks a mode,
 * nothing ever gets posted, and no screen says why.
 */
export const COMMENT_SOURCE_BLOCKER_CODES = [
  /** No usable BYOK row and the platform has no fallback key either. */
  'AI_KEY_NOT_CONFIGURED',
  /** The workspace had a key and revoked it; nothing fell back. */
  'AI_KEY_REVOKED',
  /** No BYOK row, and the API has not observed enough to say either way. */
  'AI_AVAILABILITY_UNKNOWN',
  /** Nothing is consuming the `ai-comment` queue, so a draft request times out. */
  'AI_DRAFT_WORKER_OFFLINE',
  /** The queue could not be probed; treat AI drafting as unproven. */
  'AI_DRAFT_WORKER_UNKNOWN',
  /** `TEMPLATE_AUTO` with an empty template library has nothing to send. */
  'NO_ACTIVE_TEMPLATE',
  /** The kill switch is on: the mode is selectable, but nothing will go out. */
  'EMERGENCY_STOP_ACTIVE',
] as const;
export const commentSourceBlockerCodeSchema = z.enum(COMMENT_SOURCE_BLOCKER_CODES);
export type CommentSourceBlockerCode = (typeof COMMENT_SOURCE_BLOCKER_CODES)[number];

export const commentSourceBlockerSchema = z.object({
  code: commentSourceBlockerCodeSchema,
  /** BLOCKING: this mode cannot produce a comment. WARNING: it may not. */
  severity: z.enum(['BLOCKING', 'WARNING']),
  /** Vietnamese; safe to render verbatim. */
  message: z.string(),
  /** The one action that clears it, in Vietnamese. */
  remedy: z.string(),
});
export type CommentSourceBlocker = z.infer<typeof commentSourceBlockerSchema>;

/** What actually writes the text under each mode. */
export const COMMENT_CONTENT_ORIGINS = ['NONE', 'HUMAN', 'TEMPLATE', 'AI'] as const;
export const commentContentOriginSchema = z.enum(COMMENT_CONTENT_ORIGINS);
export type CommentContentOrigin = (typeof COMMENT_CONTENT_ORIGINS)[number];

export const commentSourceOptionSchema = z.object({
  mode: commentModeSchema,
  /** Vietnamese label and one-line explanation, owned by the server. */
  label: z.string(),
  description: z.string(),
  contentOrigin: commentContentOriginSchema,
  /** True when the server may dispatch without a human in the loop. */
  automatic: z.boolean(),
  /** What approval this mode would require if selected today. */
  requiresApproval: z.boolean(),
  /** False when at least one blocker is BLOCKING. */
  selectable: z.boolean(),
  blockers: z.array(commentSourceBlockerSchema),
});
export type CommentSourceOption = z.infer<typeof commentSourceOptionSchema>;

/**
 * What the API can actually establish about AI credentials.
 *
 * Four values rather than a boolean because the API genuinely cannot see a
 * platform key: it holds neither `OPENAI_API_KEY` nor the BYOK master key. A
 * two-valued flag would have to lie in one direction or the other, so the
 * uncertainty is named instead.
 */
export const AI_CREDENTIAL_STATES = [
  /** `ai_provider_settings` has a live row for this workspace. */
  'WORKSPACE_KEY',
  /** No BYOK row, but AI calls for this workspace have recently succeeded. */
  'PLATFORM_KEY',
  /** No BYOK row, and recent AI calls were skipped for want of credentials. */
  'NOT_AVAILABLE',
  /**
   * The workspace HAD a key and revoked it, and nothing took over.
   *
   * Distinct from `NOT_AVAILABLE` because the two lead somewhere different: one
   * operator has never set a key up, the other deliberately removed one and may
   * not realise the AI sources went dark with it. Collapsing them told the
   * second person "chưa có khoá", which reads as a mistake they have not made.
   * It is also the state that makes `AI_KEY_REVOKED` — declared in the blocker
   * vocabulary from the start — actually reachable.
   */
  'REVOKED',
  /** No BYOK row and nothing observed yet. */
  'UNKNOWN',
] as const;
export const aiCredentialStateSchema = z.enum(AI_CREDENTIAL_STATES);
export type AiCredentialState = (typeof AI_CREDENTIAL_STATES)[number];

export const commentSourceCapabilitiesSchema = z.object({
  aiCredentialState: aiCredentialStateSchema,
  /** Vietnamese explanation of how that state was established. */
  aiCredentialDetail: z.string(),
  /** Last four of the workspace's own key. Never more than four characters. */
  aiKeyLastFour: z.string().length(4).nullable(),
  /** Null when the queue could not be probed — not the same as "offline". */
  aiDraftWorkerOnline: z.boolean().nullable(),
  activeTemplateCount: z.number().int().min(0),
  templateCount: z.number().int().min(0),
  emergencyStopActive: z.boolean(),
});
export type CommentSourceCapabilities = z.infer<typeof commentSourceCapabilitiesSchema>;

export const commentSourceOptionsResponseSchema = z.object({
  projectId: idSchema,
  currentMode: commentModeSchema,
  capabilities: commentSourceCapabilitiesSchema,
  /** Every mode, in `COMMENT_MODES` order — including the unselectable ones. */
  options: z.array(commentSourceOptionSchema),
  checkedAt: isoDateTimeSchema,
});
export type CommentSourceOptionsResponse = z.infer<typeof commentSourceOptionsResponseSchema>;

// ---------------------------------------------------------------------------
// GET / PUT /projects/:id/comment-settings
// ---------------------------------------------------------------------------

export const commentSettingsSchema = z.object({
  projectId: idSchema,
  mode: commentModeSchema,
  /** AI_DRAFT always queues for approval regardless of this flag. */
  requireApproval: z.boolean(),
  /** Display-scale threshold a post must clear before a candidate is created. */
  minFinalScore10: score10Schema,
  /** Model self-reported confidence, 0.0-1.0. Below this the draft is held. */
  minAiConfidence: z.number().min(0).max(1),
  minContentLength: z.number().int().min(1).max(2_000),
  maxContentLength: z.number().int().min(1).max(2_000),
  /** Per browser profile — the three windows are checked atomically in Redis. */
  maxPerProfile10m: z.number().int().min(0).max(50),
  maxPerProfile1h: z.number().int().min(0).max(200),
  maxPerProfile1d: z.number().int().min(0).max(1_000),
  maxPerProjectPerDay: z.number().int().min(0).max(1_000),
  minIntervalSeconds: z.number().int().min(0).max(86_400),
  allowLinks: z.boolean(),
  allowPhoneNumbers: z.boolean(),
  prohibitedWords: z.array(z.string().trim().min(1).max(80)).max(500),
  prohibitedLinkDomains: z.array(z.string().trim().min(1).max(200)).max(500),
  /**
   * House style handed to the model when it drafts. Stored per project and
   * exposed here because it is the only knob that shapes *what an AI comment
   * sounds like*; leaving it write-only in the database meant nobody could set
   * it and every AI draft used the model's default voice.
   */
  brandVoice: z.string().max(2_000).nullable(),
  /**
   * The buyer intent this project hunts, as one phrase ("làm thiệp cưới online").
   * When set, the AI draft prompt asks the yes/no question first — does the post
   * show this intent? — and declines when the answer is no.
   */
  targetIntent: z.string().trim().min(1).max(300).nullable(),
  /**
   * Spellings of the business website, one per entry ("traothiep.vn",
   * "traothiepvn", "traothiep chấm vn"). One variant is picked per AI draft,
   * rotated, so the same literal link string is not repeated under every post.
   * Empty means the AI never mentions a website.
   */
  websiteVariants: z.array(z.string().trim().min(1).max(120)).max(20),
  /**
   * Read-only mirror of the kill switch. It is flipped through
   * `POST /comments/emergency-stop`, never through this object — a setting that
   * can be cleared by a routine settings save is not a kill switch.
   */
  emergencyStop: z.boolean(),
  updatedAt: isoDateTimeSchema,
});
export type CommentSettings = z.infer<typeof commentSettingsSchema>;

/**
 * `emergencyStop` is deliberately not writable here: see the field comment.
 * Cross-field rules that involve a value the caller did NOT send (a partial
 * update that lowers `maxPerProfile1h` under an untouched `maxPerProfile10m`)
 * cannot be decided from the body alone and are re-checked server-side against
 * the merged row.
 */
export const updateCommentSettingsRequestSchema = commentSettingsSchema
  .omit({ projectId: true, updatedAt: true, emergencyStop: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  })
  .refine(
    (value) =>
      value.minContentLength === undefined ||
      value.maxContentLength === undefined ||
      value.minContentLength <= value.maxContentLength,
    { message: 'minContentLength phải nhỏ hơn hoặc bằng maxContentLength', path: ['minContentLength'] },
  );
export type UpdateCommentSettingsRequest = z.infer<typeof updateCommentSettingsRequestSchema>;

export const commentSettingsResponseSchema = z.object({
  settings: commentSettingsSchema,
  /**
   * Everything wrong with the mode this project is *currently* in, computed by
   * the server on every read and write.
   *
   * A project can sit in `AI_AUTO` on a workspace with no AI key: the setting is
   * legal, and the consequence is that every draft is skipped. The dashboard is
   * not asked to work that out — it renders this list. Blocking entries mean the
   * mode cannot produce a comment at all.
   */
  warnings: z.array(commentSourceBlockerSchema),
});
export type CommentSettingsResponse = z.infer<typeof commentSettingsResponseSchema>;

// ---------------------------------------------------------------------------
// Comment templates
// ---------------------------------------------------------------------------

/**
 * Alternate wordings of the same template.
 *
 * A template library of one sentence cannot be used automatically: the second
 * post gets the identical body, `validateCommentContent` refuses it as
 * DUPLICATE_CONTENT, and TEMPLATE_AUTO stalls having posted exactly once. That
 * is also the pattern Facebook reads as spam, so rotation is a safety
 * requirement rather than a stylistic one.
 *
 * `content` is variant zero. The picker chooses whichever wording is least
 * similar to what the project has sent recently.
 */
export const commentTemplateVariantsSchema = z
  .array(z.string().trim().min(1).max(2_000))
  .max(20);

/**
 * Relative preference between templates, 1-100.
 *
 * Only breaks ties: least-recently-used still comes first, because rotation is
 * what keeps bodies apart. A weight cannot pin one template permanently.
 */
export const commentTemplateWeightSchema = z.number().int().min(1).max(100);

export const commentTemplateSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  name: z.string(),
  content: z.string(),
  variants: commentTemplateVariantsSchema,
  weight: commentTemplateWeightSchema,
  tags: z.array(z.string()),
  /** Intent this template answers; used to pick a template for a post. */
  intent: postIntentSchema.nullable(),
  /** Object-storage media ids; the bytes live in S3/R2, never in Postgres. */
  mediaIds: z.array(idSchema),
  isActive: z.boolean(),
  usageCount: z.number().int().min(0),
  lastUsedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type CommentTemplate = z.infer<typeof commentTemplateSchema>;

export const createCommentTemplateRequestSchema = z.object({
  projectId: idSchema,
  name: z.string().trim().min(1).max(120),
  content: z.string().trim().min(1).max(2_000),
  variants: commentTemplateVariantsSchema.default([]),
  weight: commentTemplateWeightSchema.default(1),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  intent: postIntentSchema.optional(),
  mediaIds: z.array(idSchema).max(4).default([]),
  isActive: z.boolean().default(true),
});
export type CreateCommentTemplateRequest = z.infer<typeof createCommentTemplateRequestSchema>;

export const updateCommentTemplateRequestSchema = createCommentTemplateRequestSchema
  .omit({ projectId: true })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateCommentTemplateRequest = z.infer<typeof updateCommentTemplateRequestSchema>;

export const commentTemplateListQuerySchema = paginationQuerySchema.extend({
  projectId: idSchema,
  isActive: queryBooleanSchema.optional(),
  intent: postIntentSchema.optional(),
  search: z.string().trim().max(120).optional(),
});
export type CommentTemplateListQuery = z.infer<typeof commentTemplateListQuerySchema>;

export const commentTemplateListResponseSchema = paginated(commentTemplateSchema);
export type CommentTemplateListResponse = z.infer<typeof commentTemplateListResponseSchema>;

export const deleteCommentTemplateResponseSchema = z.object({
  templateId: idSchema,
  deletedAt: isoDateTimeSchema,
  /** Media released with the template; storage deletes them asynchronously. */
  releasedMediaIds: z.array(idSchema),
});
export type DeleteCommentTemplateResponse = z.infer<typeof deleteCommentTemplateResponseSchema>;

// ---------------------------------------------------------------------------
// Validation verdict — shared by generation and job creation
// ---------------------------------------------------------------------------

/**
 * Result of the content policy pass. `passed: false` is a normal outcome, not
 * an error: the draft is kept so an operator can see *why* it was refused.
 */
export const commentValidationSchema = z.object({
  passed: z.boolean(),
  rejectionReasons: z.array(commentRejectionReasonSchema),
  /** Human-readable detail per reason, in the same order. */
  details: z.array(z.string()),
  checkedAt: isoDateTimeSchema,
});
export type CommentValidation = z.infer<typeof commentValidationSchema>;

// ---------------------------------------------------------------------------
// POST /posts/:id/generate-comment
// ---------------------------------------------------------------------------

export const generateCommentRequestSchema = z.object({
  projectId: idSchema,
  /** Forces a specific template; otherwise the server picks one that has not
   *  been used recently, or asks the model when the mode is AI-driven. */
  templateId: idSchema.optional(),
  source: commentSourceSchema.default('AI'),
  style: z.enum(['FRIENDLY', 'PROFESSIONAL', 'CONCISE', 'ENTHUSIASTIC']).default('FRIENDLY'),
  maxLength: z.number().int().min(20).max(2_000).optional(),
  includeLink: z.boolean().default(false),
  extraInstructions: z.string().trim().max(1_000).optional(),
});
export type GenerateCommentRequest = z.infer<typeof generateCommentRequestSchema>;

/**
 * One claim in the draft that the operator's own material does not back up.
 *
 * The wire form of {@link UnsupportedClaim}, which `comment-validation.ts`
 * produces as a pure value and which is now also a stored column and a rendered
 * list. `quote` is the exact phrase from the draft, so a reviewer can judge
 * "giảm 30%" without re-reading the whole body hunting for what was flagged —
 * which is the entire reason this is structured data rather than a sentence
 * appended to `reason` and truncated at 2 000 characters with everything else.
 */
export const unsupportedClaimSchema = z.object({
  kind: unsupportedClaimKindSchema,
  quote: z.string(),
});

/**
 * Compile-time proof that the wire shape and the validator's own interface have
 * not drifted apart.
 *
 * They are declared in two files because the validator must stay pure and
 * dependency-free while the wire shape needs the enum vocabulary. A drift would
 * be silent — the API would happily persist a `kind` the dashboard cannot render
 * — so it is made a type error instead. Both directions are asserted: adding a
 * field to either side breaks the build.
 */
type AssertAssignable<Narrow extends Wide, Wide> = Narrow extends Wide ? true : never;
type _WireIsAValidClaim = AssertAssignable<z.infer<typeof unsupportedClaimSchema>, UnsupportedClaim>;
type _ClaimIsValidOnTheWire = AssertAssignable<UnsupportedClaim, z.infer<typeof unsupportedClaimSchema>>;

export const commentCandidateSchema = z.object({
  id: idSchema,
  postId: idSchema,
  postMatchId: idSchema,
  projectId: idSchema,
  source: commentSourceSchema,
  content: z.string(),
  /** Model self-assessment, 0.0-1.0. Null for template and manual sources. */
  confidence: z.number().min(0).max(1).nullable(),
  /**
   * The OR of every entry in `reviewFlags`, kept for callers that only need to
   * know *whether* a person must look. Read `reviewFlags` to find out why —
   * this boolean is true for almost every candidate the product creates, because
   * `require_approval` defaults to true.
   */
  requiresManualReview: z.boolean(),
  /**
   * Each separate reason this draft is waiting, in `COMMENT_REVIEW_FLAGS` order.
   *
   * Empty for a draft nothing is wrong with. Always present: a candidate written
   * before this field existed answers `[]` rather than `undefined`, so a client
   * may iterate it without a guard.
   */
  reviewFlags: z.array(commentReviewFlagSchema),
  /**
   * The specific sentences behind an `UNSUPPORTED_CLAIM` flag, quoted verbatim.
   * Empty whenever that flag is absent.
   */
  unsupportedClaims: z.array(unsupportedClaimSchema),
  reason: z.string(),
  templateId: idSchema.nullable(),
  selectedMediaId: idSchema.nullable(),
  validation: commentValidationSchema,
  createdAt: isoDateTimeSchema,
});
export type CommentCandidate = z.infer<typeof commentCandidateSchema>;

export const generateCommentResponseSchema = z.object({
  candidate: commentCandidateSchema,
  /**
   * Non-null ONLY when the server both approved this itself and got the command
   * onto the agent's socket. Null in every other case — awaiting approval,
   * refused by the policy engine at dispatch, or dispatched to an agent whose
   * socket had closed.
   *
   * The last case is why `commandStatus` exists. `jobId` alone cannot separate
   * "a person must approve this" from "a command was written but never left this
   * process", and the difference matters: the second one has a real QUEUED job
   * that nothing will ever pick up, because the server does not redeliver a
   * command an offline agent missed.
   */
  jobId: idSchema.nullable(),
  /** The `comment.execute` command, when a dispatch was attempted at all. */
  commandId: ulidSchema.nullable(),
  /**
   * The truth about that command, three-valued on purpose:
   *   - `null`   — no dispatch was attempted; the job is waiting for a human.
   *   - `PENDING` — a command row exists, the frame did NOT leave the server.
   *   - `SENT`    — the frame was written to the agent's socket.
   *
   * A client must not say "đã gửi đi" on anything but `SENT`.
   */
  commandStatus: agentCommandStatusSchema.nullable(),
});
export type GenerateCommentResponse = z.infer<typeof generateCommentResponseSchema>;

// ---------------------------------------------------------------------------
// Comment jobs
// ---------------------------------------------------------------------------

export const commentJobSchema = z.object({
  id: idSchema,
  projectId: idSchema,
  /** The channel the comment will be posted on — the post's own platform.
   *  Defaulted so a response from an older server still parses. */
  platform: platformSchema.default('FACEBOOK'),
  postId: idSchema,
  postMatchId: idSchema,
  candidateId: idSchema.nullable(),
  agentId: idSchema.nullable(),
  browserProfileId: idSchema.nullable(),
  status: commentJobStatusSchema,
  source: commentSourceSchema,
  content: z.string(),
  mediaIds: z.array(idSchema),
  templateId: idSchema.nullable(),
  attemptCount: z.number().int().min(0),
  scheduledAt: isoDateTimeSchema.nullable(),
  approvedAt: isoDateTimeSchema.nullable(),
  approvedByUserId: idSchema.nullable(),
  sentToAgentAt: isoDateTimeSchema.nullable(),
  /** Set once the job leaves as a `comment.execute` command. */
  commandId: ulidSchema.nullable(),
  commandExpiresAt: isoDateTimeSchema.nullable(),
  rejectionReason: commentRejectionReasonSchema.nullable(),
  errorMessage: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type CommentJob = z.infer<typeof commentJobSchema>;

export const commentJobListQuerySchema = paginationQuerySchema.extend({
  projectId: idSchema.optional(),
  postId: idSchema.optional(),
  status: z.union([commentJobStatusSchema, z.array(commentJobStatusSchema)]).optional(),
  source: commentSourceSchema.optional(),
  browserProfileId: idSchema.optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  /** Convenience filter for the approval queue (status WAITING_APPROVAL). */
  awaitingApproval: queryBooleanSchema.optional(),
  sortOrder: sortOrderSchema,
});
export type CommentJobListQuery = z.infer<typeof commentJobListQuerySchema>;

export const commentJobPostSummarySchema = z.object({
  postId: idSchema,
  permalink: z.string().url().nullable(),
  contentExcerpt: z.string(),
  authorName: z.string().nullable(),
  finalScore10: score10Schema.nullable(),
});
export type CommentJobPostSummary = z.infer<typeof commentJobPostSummarySchema>;

export const commentJobListItemSchema = commentJobSchema.extend({
  post: commentJobPostSummarySchema,
});
export type CommentJobListItem = z.infer<typeof commentJobListItemSchema>;

export const commentJobListResponseSchema = paginated(commentJobListItemSchema);
export type CommentJobListResponse = z.infer<typeof commentJobListResponseSchema>;

// ---------------------------------------------------------------------------
// Comment results
// ---------------------------------------------------------------------------

export const commentResultSchema = z.object({
  id: idSchema,
  jobId: idSchema,
  /** Terminal status of the attempt this result belongs to. */
  status: commentJobStatusSchema,
  succeeded: z.boolean(),
  externalCommentId: z.string().nullable(),
  commentUrl: z.string().url().nullable(),
  rejectionReason: commentRejectionReasonSchema.nullable(),
  errorMessage: z.string().nullable(),
  /**
   * What the page said about a comment that DID go out, quoted by the agent —
   * the success-side counterpart of `errorMessage`, and never an explanation of
   * a failure. A Threads reply on a post whose author gates replies is created
   * but invisible ("Đang chờ tác giả phê duyệt"), and this is where that
   * travels. Defaulted so a response from an older server still parses.
   */
  note: z.string().nullable().default(null),
  /** Set when the agent's own guard refused; its refusal takes precedence. */
  rejectedByAgent: z.boolean(),
  /** Diagnostic screenshot in object storage; self-expires after 7-30 days. */
  screenshotMediaId: idSchema.nullable(),
  durationMs: z.number().int().min(0).nullable(),
  executedAt: isoDateTimeSchema,
});
export type CommentResult = z.infer<typeof commentResultSchema>;

export const commentJobDetailResponseSchema = z.object({
  job: commentJobSchema,
  post: commentJobPostSummarySchema,
  candidate: commentCandidateSchema.nullable(),
  results: z.array(commentResultSchema),
  /** Transitions the API will currently accept, from COMMENT_JOB_TRANSITIONS. */
  allowedTransitions: z.array(commentJobStatusSchema),
});
export type CommentJobDetailResponse = z.infer<typeof commentJobDetailResponseSchema>;

export const commentResultListQuerySchema = paginationQuerySchema.extend({
  projectId: idSchema.optional(),
  jobId: idSchema.optional(),
  browserProfileId: idSchema.optional(),
  succeeded: queryBooleanSchema.optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  sortOrder: sortOrderSchema,
});
export type CommentResultListQuery = z.infer<typeof commentResultListQuerySchema>;

export const commentResultListResponseSchema = paginated(commentResultSchema);
export type CommentResultListResponse = z.infer<typeof commentResultListResponseSchema>;

// ---------------------------------------------------------------------------
// POST /comment-jobs/:id/{approve|cancel}
// ---------------------------------------------------------------------------

export const approveCommentJobRequestSchema = z.object({
  /** An edited body is re-validated exactly like a generated one. */
  editedContent: z.string().trim().min(1).max(2_000).optional(),
  mediaIds: z.array(idSchema).max(4).optional(),
  note: z.string().trim().max(500).optional(),
});
export type ApproveCommentJobRequest = z.infer<typeof approveCommentJobRequestSchema>;

export const approveCommentJobResponseSchema = z.object({
  job: commentJobSchema,
  validation: commentValidationSchema,
  /** Null when validation or a rate limit held the job back after approval. */
  commandId: ulidSchema.nullable(),
  commandStatus: agentCommandStatusSchema.nullable(),
});
export type ApproveCommentJobResponse = z.infer<typeof approveCommentJobResponseSchema>;

export const cancelCommentJobRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type CancelCommentJobRequest = z.infer<typeof cancelCommentJobRequestSchema>;

/**
 * Cancelling writes CANCELLED to the row. Whether that stops a comment being
 * typed is a *different question*, and this response answers it separately.
 *
 * `COMMENT_JOB_TRANSITIONS` permits SENT_TO_AGENT → CANCELLED, so the button is
 * live for a job whose `comment.execute` frame is already on the operator's own
 * machine. The row always changes; the comment is only provably stopped in the
 * two outcomes listed in `GUARANTEED_COMMENT_CANCEL_OUTCOMES`.
 */
export const cancelCommentJobResponseSchema = z.object({
  jobId: idSchema,
  status: commentJobStatusSchema,
  cancelledAt: isoDateTimeSchema,
  /** How far the recall reached. See `COMMENT_CANCEL_RECALL_OUTCOMES`. */
  recallOutcome: commentCancelRecallOutcomeSchema,
  /**
   * True only when the server can prove nothing will be posted.
   *
   * False does NOT mean the comment went out — it means the server does not
   * know, because the instruction had already left it. A client rendering a
   * confident "Đã huỷ" on `false` is re-creating the bug this field exists to
   * end; say that the recall was attempted and that the job must be checked.
   */
  guaranteedNotPosted: z.boolean(),
  /** Vietnamese, safe to render verbatim; states exactly what was stopped. */
  recallMessage: z.string(),
  /** The command the cancellation tried to withdraw; null when there was none. */
  revokedCommandId: ulidSchema.nullable(),
});
export type CancelCommentJobResponse = z.infer<typeof cancelCommentJobResponseSchema>;
